import React from 'react';
import { SectionLabel } from '../shared/SurfaceParts';
import { entropyBarClass } from '../entropyLanguage';
import type { BridgeSessionAssessmentModel } from './bridgeSessionAssessment';
import styles from '../ControlBridge.module.css';

export interface MissionControlWorkshopAssessmentProps {
  model: BridgeSessionAssessmentModel;
  compact?: boolean;
}

function EntropyCell({ entropy, label }: { entropy: number; label: string }) {
  const tone = entropyBarClass(entropy);
  const fill = Math.max(8, Math.min(100, (entropy / 10) * 100));
  return (
    <div className={styles.mcWorkshopEntropyCell} role="listitem">
      <span className={styles.mcWorkshopEntropyLabel}>{label}</span>
      <div className={styles.mcProgressWrap} role="img" aria-label={`${label} entropy ${entropy.toFixed(1)}`}>
        <div className={styles.mcProgressTrack}>
          <div
            className={`${styles.mcProgressFill} ${styles[`mcProgressFill_${tone}`]}`}
            style={{ width: `${fill}%` }}
          />
        </div>
      </div>
      <span className={styles.mcWorkshopEntropyValue}>{entropy.toFixed(1)}</span>
    </div>
  );
}

function CompactSummary({ model }: { model: BridgeSessionAssessmentModel }) {
  const avgEntropy =
    model.domainEntropy.length > 0
      ? model.domainEntropy.reduce((sum, row) => sum + row.entropy, 0) / model.domainEntropy.length
      : null;
  const topFriction = model.frictionPriorities[0];
  return (
    <div className={styles.mcWorkshopCompact} data-testid="mc-workshop-assessment-compact">
      {avgEntropy != null ? (
        <span className={styles.mcWorkshopCompactChip}>
          Workshop avg entropy {avgEntropy.toFixed(1)}
        </span>
      ) : null}
      {topFriction ? (
        <span className={styles.mcWorkshopCompactChip} title={topFriction.text}>
          Top friction: {topFriction.text.length > 56 ? `${topFriction.text.slice(0, 56)}…` : topFriction.text}
        </span>
      ) : null}
    </div>
  );
}

export const MissionControlWorkshopAssessment: React.FC<MissionControlWorkshopAssessmentProps> = ({
  model,
  compact = false,
}) => {
  const showHeatMap = model.domainEntropy.length > 0;
  const showFriction = model.frictionPriorities.length > 0;
  if (!showHeatMap && !showFriction) return null;

  if (compact) {
    return <CompactSummary model={model} />;
  }

  return (
    <div data-testid="mc-workshop-assessment">
      {showHeatMap ? (
        <section aria-label="Workshop entropy heat map">
          <SectionLabel>Workshop entropy heat map</SectionLabel>
          {model.bridgeSessionName ? (
            <p className={styles.mcWorkshopSessionLabel}>{model.bridgeSessionName}</p>
          ) : null}
          <div className={styles.mcWorkshopEntropyGrid} role="list">
            {model.domainEntropy.map((row) => (
              <EntropyCell key={row.scope} entropy={row.entropy} label={row.scope} />
            ))}
          </div>
        </section>
      ) : null}

      {showFriction ? (
        <section aria-label="Friction priorities">
          <SectionLabel>Friction priorities</SectionLabel>
          <ol className={styles.mcFrictionList}>
            {model.frictionPriorities.map((row, index) => (
              <li key={`${row.questionId}-${index}`} className={styles.mcFrictionItem}>
                <span className={styles.mcFrictionRank}>{index + 1}</span>
                <div className={styles.mcFrictionBody}>
                  <span className={styles.mcFrictionText}>{row.text}</span>
                  <span className={styles.mcFrictionMeta}>
                    {[row.domain, row.factor, row.score != null ? `score ${row.score}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  );
};
